/**
 * TimelineNavigation Component
 *
 * Navigation controls for moving through time periods in the Sales Timeline.
 * Displays the current period label with previous/next buttons and a today button.
 *
 * US-PE-006: Sales Timeline - Phase 3 Page Components
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { createDate } from '@/utils/dateUtils'

/**
 * Format the period label based on view mode
 *
 * @param {Object} dateRange - { start, end } date strings in YYYY-MM-DD format
 * @param {string} viewMode - '7d' | '14d' | 'month'
 * @returns {string} Formatted period label
 */
function formatPeriodLabel(dateRange, viewMode) {
  if (!dateRange?.start || !dateRange?.end) return ''

  const start = createDate(dateRange.start)
  const end = createDate(dateRange.end)

  const startDay = start.getDate()
  const endDay = end.getDate()
  const startMonth = start.toLocaleDateString('en-GB', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-GB', { month: 'short' })
  const startYear = start.getFullYear()
  const endYear = end.getFullYear()

  // For month view, show just the month name and year
  if (viewMode === 'month') {
    // Use the middle of the range to determine the primary month
    // This handles cases where the range spans month boundaries
    const midDate = createDate(start.getTime())
    midDate.setDate(midDate.getDate() + 15) // ~middle of a month view
    return midDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  }

  // For 7d and 14d views, show date range
  // Same month and year: "6 - 12 Jan 2026"
  if (startMonth === endMonth && startYear === endYear) {
    return `${startDay} - ${endDay} ${endMonth} ${endYear}`
  }

  // Same year but different months: "30 Dec - 5 Jan 2026"
  if (startYear === endYear) {
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`
  }

  // Different years: "30 Dec 2025 - 5 Jan 2026"
  return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`
}

/**
 * TimelineNavigation - Period navigation controls
 *
 * @param {Object} props
 * @param {Object} props.dateRange - { start, end } date strings in YYYY-MM-DD format
 * @param {string} props.viewMode - Current view mode ('7d' | '14d' | 'month')
 * @param {Function} props.onNavigate - Callback for navigation (direction: 'prev' | 'next' | 'today')
 * @param {boolean} [props.disabled] - Disable navigation controls
 * @param {string} [props.className] - Additional CSS classes
 */
export default function TimelineNavigation({
  dateRange,
  viewMode = '7d',
  onNavigate,
  disabled = false,
  className = ''
}) {
  const periodLabel = formatPeriodLabel(dateRange, viewMode)

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Previous Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate('prev')}
        disabled={disabled}
        aria-label="Previous period"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>

      {/* Period Label */}
      <div className="min-w-48 text-center">
        <span className="text-sm font-medium text-slate-700">
          {periodLabel}
        </span>
      </div>

      {/* Next Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate('next')}
        disabled={disabled}
        aria-label="Next period"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>

      {/* Today Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate('today')}
        disabled={disabled}
      >
        Today
      </Button>
    </div>
  )
}
